import React, { memo, useEffect } from "react";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import { Divider, makeStyles } from "@material-ui/core";
import {
  createLogEntryPayload,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";

import Filters from "../../../components/RouteDetail/Filters";
import palette from "../../../components/styles/palette";
import useStopListTableProps from "../hooks/useStopListTableProps";
import { moveSelectedStops } from "../../../store/actions/route";
import {
  toggleFilter,
  setOrder,
  handleParcelRowClick,
} from "../../../store/actions/ui/routeDetail";
import {
  handleStopPostcodeChange,
  handleStopTimeChange,
} from "../../../store/actions/stop";

import {
  getFilteredStops,
  getMoveButtonsDisabledState,
  getStopFilters,
  getStopListOrder,
} from "../../../store/selectors/ui/routeDetail";
import {
  useBodyCellStopListStyles,
  useHeaderCellStopListStyles,
} from "../../../components/styles/tablesCells";
import { STOP_LIST } from "../../../constants/analytics";
import { STOP_QUICK_FILTERS } from "../../../constants/analytics";
import { View } from "../../../components/Base";
import VirtualizedTable from "../../../components/common/Table/VirtualizedTable";

const useStyles = makeStyles(({ typography }) => ({
  bottomContainer: {
    bottom: 0,
    width: "100%",
    display: "flex",
    flexFlow: "column",
    position: "absolute",
    backgroundColor: "white",
    boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.25)",
    height: `calc(100% - ${typography.pxToRem(430)})`,
  },
}));

const useTableStyles = makeStyles(({ typography }) => ({
  tableWrap: {
    userSelect: "none",
    overflow: "auto",
    width: "100%",
    height: "100%",
    display: "flex",
  },
  table: {
    height: "100%",
  },
  headerCell: {
    background: palette.stopListHeader,
    verticalAlign: "bottom",
  },
  selectedRow: {
    background: palette.selectedStopListRow,
  },
  sortArrow: {
    paddingBottom: typography.pxToRem(8),
    paddingLeft: 3,
  },
  headerCellContainer: {
    display: "flex",
    alignItems: "flex-end",
    background: palette.stopListHeader,
  },
  gridContainer: {
    paddingBottom: typography.pxToRem(14),
    scrollbarWidth: "thin",
  },
  cellClassName: {
    height: "100%",
  },
}));

const StopList = ({
  toggleFilter,
  setOrder,
  handleParcelRowClick,
  handleStopTimeChange,
  handleStopPostcodeChange,
  onSelectStopRequests,
  moveSelectedStops,
  filteredStops,
  moveButtonsState,
  filters,
  accessor,
  isAscending,
  handleOnLoad,
}) => {
  const classes = useStyles();
  const tableClasses = useTableStyles();

  useEffect(() => {
    handleOnLoad();
  }, [handleOnLoad]);

  const stopListTableProps = useStopListTableProps(
    {
      handleStopTimeChange,
      handleStopPostcodeChange,
      onSelectStopRequests,
    },
    useHeaderCellStopListStyles(),
    useBodyCellStopListStyles()
  );

  return (
    <View className={classes.bottomContainer}>
      <Filters
        filters={filters}
        toggleFilter={toggleFilter}
        filteredStops={filteredStops}
        moveStops={moveSelectedStops}
        moveButtonsState={moveButtonsState}
      />
      <Divider />
      <VirtualizedTable
        handleRowClick={handleParcelRowClick}
        classes={tableClasses}
        isStopList
        isAscending={isAscending}
        order={accessor}
        setOrder={setOrder}
        stickyHeader
        tableWidth={1460}
        headerHeight={48}
        rowHeight={28}
        overscanRowCount={10}
        withFooter={true}
        {...stopListTableProps}
      />
    </View>
  );
};

const mapStateToProps = (state) => {
  const { filters } = getStopFilters(state);
  const { accessor, isAscending } = getStopListOrder(state);
  return {
    filteredStops: getFilteredStops(state),
    moveButtonsState: getMoveButtonsDisabledState(state),
    filters,
    accessor,
    isAscending,
  };
};

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(
    {
      toggleFilter,
      setOrder,
      handleParcelRowClick,
      handleStopTimeChange,
      handleStopPostcodeChange,
    },
    dispatch
  ),
  moveSelectedStops: (direction) => dispatch(moveSelectedStops({ direction })),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTrackProps({
    setOrder: (accessor) =>
      createLogEntryPayload(STOP_LIST.ON_SORT_COLUMN, { accessor }),
    handleStopTimeChange: (value) =>
      createLogEntryPayload(STOP_LIST.ON_CHANGE_STOP_TIME, { value }),
    handleStopPostcodeChange: (value) =>
      createLogEntryPayload(STOP_LIST.ON_CHANGE_POSTCODE, { value }),
    onSelectStopRequests: (stop) =>
      createLogEntryPayload(STOP_LIST.ON_CLICK_REQUEST, { stop }),
    handleParcelRowClick: STOP_LIST.ON_SELECT_ROW,
    moveSelectedStops: (value) =>
      createLogEntryPayload(STOP_LIST.ON_SHIFT_CLICK, { value }),
    toggleFilter: (id) => STOP_QUICK_FILTERS[id],
    handleOnLoad: STOP_LIST.LOAD,
  })
)(memo(StopList));
